import { type OperatorScope, type ScopeLocationHint } from '../src/index';

/**
 * The browser reads behind the starting scope (owner, 2026-10-12; the rule is `preferredScope` in `src/scope.ts`).
 * Nothing here leaves the device: the position is compared with the market centers in memory and dropped.
 */

/** How long the location prompt may take before the console keeps the time-zone guess. */
const LOCATE_TIMEOUT_MS = 8_000;
/** A position up to an hour old is good enough to pick a city. */
const LOCATE_MAX_AGE_MS = 3_600_000;

/** The browser's IANA time zone, when it reports one. */
export function browserTimeZone(): string | undefined {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return zone === '' ? undefined : zone;
  } catch {
    return undefined;
  }
}

/** The browser's coarse position, or `undefined` when there is no geolocation, the operator refuses it, or it times out. */
export function locateBrowser(): Promise<ScopeLocationHint['position']> {
  const geo = typeof navigator === 'undefined' ? undefined : navigator.geolocation;
  if (geo === undefined) return Promise.resolve(undefined);
  return new Promise((resolve) => {
    geo.getCurrentPosition(
      (p) => resolve({ latitude: p.coords.latitude, longitude: p.coords.longitude }),
      () => resolve(undefined),
      { enableHighAccuracy: false, timeout: LOCATE_TIMEOUT_MS, maximumAge: LOCATE_MAX_AGE_MS },
    );
  });
}

const rememberKey = (userId: string): string => `vello.admin.scope.${userId}`;

/** The scope this operator last chose on this browser (a per-viewer convenience; storage may be unavailable). */
export function rememberedScope(userId: string): OperatorScope | undefined {
  try {
    const raw = globalThis.localStorage?.getItem(rememberKey(userId));
    if (raw === null || raw === undefined) return undefined;
    // A scope stored before the state level has no `region`: it reads as the same country / city scope it always was.
    const v = JSON.parse(raw) as { country?: unknown; region?: unknown; city?: unknown };
    return {
      ...(typeof v.country === 'string' ? { country: v.country } : {}),
      ...(typeof v.region === 'string' ? { region: v.region } : {}),
      ...(typeof v.city === 'string' ? { city: v.city } : {}),
    };
  } catch {
    return undefined;
  }
}

/** Remember the scope the operator chose, so the next visit opens there instead of guessing again. */
export function rememberScope(userId: string, scope: OperatorScope): void {
  try {
    globalThis.localStorage?.setItem(rememberKey(userId), JSON.stringify(scope));
  } catch {
    // Storage blocked (private window, policy): the next visit guesses from the location again.
  }
}
